// API Services for CryptoPanic and Marketaux
// You'll need to add your API keys to environment variables

import { fallbackNews } from "./fallbackNews.data";

const CRYPTOPANIC_API_KEY = process.env.REACT_APP_CRYPTOPANIC_API_KEY;
const MARKETAUX_API_KEY = process.env.REACT_APP_MARKETAUX_API_KEY;

// CryptoPanic API for crypto news
export const fetchCryptoNews = async (limit = 10) => {
  try {
    const response = await fetch(
      `https://cryptopanic.com/api/v1/posts/?auth_token=${CRYPTOPANIC_API_KEY}&filter=hot&currencies=BTC,ETH,ADA,SOL,XRP&public=true&limit=${limit}`
    );

    if (!response.ok) {
      throw new Error("Failed to fetch crypto news");
    }

    const data = await response.json();
    return data.results || [];
  } catch (error) {
    console.error("Error fetching crypto news:", error);
    return [];
  }
};

// Marketaux API for commodities news
export const fetchCommoditiesNews = async (limit = 10) => {
  try {
    const response = await fetch(
      `https://api.marketaux.com/v1/news/all?api_token=${MARKETAUX_API_KEY}&filter_entities=true&language=en&limit=${limit}&exchanges=FOREX,COMMODITY`
    );

    if (!response.ok) {
      throw new Error("Failed to fetch commodities news");
    }

    const data = await response.json();
    return data.data || [];
  } catch (error) {
    console.error("Error fetching commodities news:", error);
    return [];
  }
};

// Transform CryptoPanic data to match our blog format
export const transformCryptoNews = (cryptoNews) => {
  return cryptoNews.map((news, index) => ({
    id: index + 1,
    title: news.title,
    excerpt: news.metadata?.description || news.title.substring(0, 100) + "...",
    category: "Crypto",
    date: new Date(news.published_at).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }),
    image:
      news.metadata?.image?.url ||
      "https://via.placeholder.com/400x250?text=Crypto+News",
    author: {
      name: news.metadata?.author || "CryptoPanic",
      avatar: "https://via.placeholder.com/40x40?text=CP",
      role: "News Source",
    },
    readTime: `${Math.ceil(news.title.length / 200)} min read`,
    likes: Math.floor(Math.random() * 500) + 50,
    url: news.url,
    source: "CryptoPanic",
  }));
};

// Transform Marketaux data to match our blog format
export const transformCommoditiesNews = (commoditiesNews) => {
  return commoditiesNews.map((news, index) => ({
    id: index + 100, // Different ID range to avoid conflicts
    title: news.title,
    excerpt: news.description || news.title.substring(0, 100) + "...",
    category: "Commodities",
    date: new Date(news.published_at).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }),
    image:
      news.image_url ||
      "https://via.placeholder.com/400x250?text=Commodities+News",
    author: {
      name: news.source || "Marketaux",
      avatar: "https://via.placeholder.com/40x40?text=MX",
      role: "Financial News",
    },
    readTime: `${Math.ceil(news.title.length / 200)} min read`,
    likes: Math.floor(Math.random() * 500) + 50,
    url: news.url,
    source: "Marketaux",
  }));
};

// Combined function to get all news
export const fetchAllNews = async () => {
  try {
    // Check if API keys are available
    if (!CRYPTOPANIC_API_KEY || !MARKETAUX_API_KEY) {
      console.warn("API keys not found. Using fallback data.");
      return fallbackNews;
    }

    const [cryptoNews, commoditiesNews] = await Promise.all([
      fetchCryptoNews(5),
      fetchCommoditiesNews(5),
    ]);

    const transformedCrypto = transformCryptoNews(cryptoNews);
    const transformedCommodities = transformCommoditiesNews(commoditiesNews);

    // Combine and shuffle the news
    const allNews = [...transformedCrypto, ...transformedCommodities];

    // If no real data was fetched, use fallback
    if (allNews.length === 0) {
      console.warn("No real data fetched. Using fallback data.");
      return fallbackNews;
    }

    return allNews.sort(() => Math.random() - 0.5);
  } catch (error) {
    console.error("Error fetching all news:", error);
    console.warn("Using fallback data due to error.");
    return fallbackNews;
  }
};
